<script setup>
import { ref, onMounted } from 'vue';
import Swal from 'sweetalert2';
import { ResourceClient } from "@/services/ResourceClient";
const resourceClient = new ResourceClient();

const props = defineProps({
  files: {
    type: Array,
    default: () => []
  },
  lessonId: {
    type: Number,
    default: null
  }
});

const files = ref([]);
const videoId = ref(null);

const emit = defineEmits(['resource-updated']);
const isUploading = ref(false);
const selectedFile = ref(null);
const MAX_VIDEO_SIZE_MB = 80000; 


// Fetch lesson resources when component mounts or lessonId changes
onMounted(async () => {
  if (props.lessonId) {
    await fetchLessonVideos();
  }
});

// Fetch lesson resources from backend
const fetchLessonVideos = async () => {
  try {
    const response = await resourceClient.getResourceById(props.lessonId, 'video');

    files.value = response.map(resource => ({
      id: resource.id,
      type: resource.tipo,
      name: resource.nombre_archivo,
      url: resource.url,
      isPreview: resource.es_visualizacion
    }));

    if (response.length > 0) {
      await loadVideo(response[0]); // Send the first video to loadVideo
    }

  } catch (error) {
    console.error("Error fetching resources:", error);
    Swal.fire('Error', 'No se pudieron cargar los recursos', 'error');
  }
};

const loadVideo = async (resource) => {
  try {
    const url = `${process.env.VUE_APP_BACK_END_URL}/resource/download-url?key=${resource.url}`;

    const response = await fetch(url, {
      headers: {
        Accept: 'text/plain',
        Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`
      }
    });

    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    videoId.value = resource.id; // Guardamos el ID para eliminarlo después

    files.value = [{
      id: resource.id,
      type: 'video',
      name: resource.nombre_archivo,
      url: resource.url,
      downloadUrl: responseText,
      isPreview: true
    }];

  } catch (error) {
    console.error('❌ Error loading video:', error);
    Swal.fire('Error', 'Failed to load test video: ' + error.message, 'error');
  }
};

const handleFileChange = (event) => {
  const file = event.target.files[0];

  if (file) {
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_VIDEO_SIZE_MB) {
      Swal.fire('Archivo demasiado grande', `El video no debe superar los ${MAX_VIDEO_SIZE_MB} MB`, 'error');
      event.target.value = ''; // limpia el input
      selectedFile.value = null;
      return;
    }

    selectedFile.value = file;
  }
};

const getChunkSize = (fileSizeInBytes) => {
  const MB = 1024 * 1024;

  if (fileSizeInBytes <= 500 * MB) {
    return 10 * MB; // 10MB
  } else if (fileSizeInBytes <= 2 * 1024 * MB) {
    return 50 * MB; // 50MB
  } else if (fileSizeInBytes <= 10 * 1024 * MB) {
    return 100 * MB; // 100MB
  } else if (fileSizeInBytes <= 50 * 1024 * MB) {
    return 200 * MB; // 200MB
  } else {
    return 500 * MB; // 500MB
  }
};

const uploadVideo = async () => {
  if (!selectedFile.value) {
    Swal.fire('Error', 'Por favor seleccione un archivo de video', 'error');
    return;
  }

  const file = selectedFile.value;
  const isLargeFile = file.size > 3000 * 1024 * 1024; // 3000 MB as threshold for large files
  isUploading.value = true;

  try {
    if (!isLargeFile) {
      // ✅ Subida simple con presigned URL
      const mimeType = file.type;
      const fileName = file.name;

      const uploadUrlResponse = await resourceClient.getUploadUrl(mimeType);
      const { url: presignedUrl, key } = uploadUrlResponse;

      await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': mimeType,
        },
      });

      await resourceClient.createResource({
        leccion_id: props.lessonId,
        tipo: mimeType,
        url: key,
        nombre_archivo: fileName,
        es_visualizacion: true,
      });

      Swal.fire('Éxito', '¡Video subido exitosamente!', 'success');
    } else {
      // 🔁 Subida multipart
      // const chunkSize = 100 * 1024 * 1024; // 100MB
      const chunkSize = getChunkSize(file.size);
      const totalChunks = Math.ceil(file.size / chunkSize);
      const token = sessionStorage.getItem("token");
      const fileName = file.name;
      const mimeType = file.type;

      // 1. Iniciar multipart upload
      const initRes = await fetch(`${process.env.VUE_APP_BACK_END_URL}/resource/multipart/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ filename: fileName, mimeType }),
      });

      const { uploadId, key } = await initRes.json();
      const parts = [];

      // 2. Subir cada parte
      for (let i = 0; i < totalChunks; i++) {
        console.log('Chunks:', totalChunks, 'Current chunk:', i + 1);
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        // Obtener presigned URL para este chunk
        const partUrlRes = await fetch(`${process.env.VUE_APP_BACK_END_URL}/resource/multipart/url?key=${encodeURIComponent(key)}&uploadId=${encodeURIComponent(uploadId)}&partNumber=${i + 1}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log('Part URL Response:', partUrlRes);

        let presignedUrl;

        try {
          // Intenta leer como JSON
          const json = await partUrlRes.clone().json(); // 👈 importante usar clone()
          presignedUrl = json.url;

          if (!presignedUrl) {
            throw new Error('El campo `url` no está presente en la respuesta JSON.');
          }
        } catch (jsonError) {
          try {
            // Fallback si no es JSON
            presignedUrl = await partUrlRes.clone().text();

            // Validar si el texto parece una URL
            if (!presignedUrl.startsWith('http')) {
              throw new Error(`Texto no válido como URL: ${presignedUrl}`);
            }
          } catch (textError) {
            console.error('❌ No se pudo obtener el presigned URL', textError);
            throw new Error(`No se pudo procesar la respuesta del servidor para partNumber`);
          }
        }

        const uploadRes = await fetch(presignedUrl, {
          method: 'PUT',
          body: chunk,
        });

        const etag = uploadRes.headers.get("ETag");if (!etag) {
          throw new Error(`No se recibió ETag para la parte ${i + 1}`);
        }

        parts.push({ ETag: etag.replace(/"/g, ""), PartNumber: i + 1 });

        console.log(`✅ Parte ${i + 1} subida`);
      }

      // 3. Crea el payload para completar la subida
      const completePayload = {
        key,
        uploadId,
        parts,
        nombre_archivo: fileName,
        tipo: mimeType,
        leccion_id: props.lessonId,
        es_visualizacion: true
      };

      // 4. Guardar metadatos
      const completeRes = await fetch(`${process.env.VUE_APP_BACK_END_URL}/resource/multipart/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(completePayload),
      });
      const result = await completeRes.json(); // <--- asegúrate de que backend devuelve JSON
      console.log("✅ Subida completa:", result);

      Swal.fire('Éxito', '¡Video subido exitosamente!', 'success');
    }

    selectedFile.value = null;
    await fetchLessonVideos();
  } catch (error) {
    console.error('Error al subir video:', error);
    Swal.fire('Error', 'No se pudo subir el video', 'error');
  } finally {
    isUploading.value = false;
  }
};


async function removeVideo() {
  try {
    if (videoId.value) {
      await resourceClient.deleteResource(videoId.value);
    }

    videoId.value = null;
    files.value = [];

    const input = document.getElementById('video-upload');
    if (input) input.value = '';

    Swal.fire('Éxito', 'Video eliminado correctamente', 'success');
  } catch (error) {
    console.error('Error al eliminar el video:', error);
    Swal.fire('Error', 'No se pudo eliminar el video', 'error');
  }
}


</script>

<template>
  <div class="row mt-4">
    <div class="col-12">
      <div class="card-body pt-0">
        <h6 class="font-weight-bolder"><i class="fab fa-youtube pe-2"></i> Video del Curso</h6>
        <div class="col-12">

          <!-- Video player (only if video exists) -->
          <div v-if="files.length && files[0].downloadUrl" class="mt-4">
            <video controls controlsList="nodownload" class="w-100 rounded shadow" :src="files[0].downloadUrl"
              style="max-height: 480px; border: 1px solid #ccc; border-radius: 8px;">
            </video>
          </div>

          <!-- Upload/Delete buttons -->
          <div class="file-upload-container mb-4 mt-4">
            <input type="file" id="video-upload" accept="video/*" @change="handleFileChange" class="d-none">

            <!-- CASE 1: No video → Show "Cargar Video" -->
            <template v-if="!files.length || !files[0]?.id">
              <div class="d-grid gap-2"> <!-- ADD THIS WRAPPER -->
                <label for="video-upload" class="btn btn-primary w-100">
                  <i class="fas fa-upload me-2"></i>
                  {{ selectedFile ? selectedFile.name : 'Cargar Video (máx. 10 GB)' }}
                </label>
                <button v-if="selectedFile" @click="uploadVideo" class="btn btn-info w-100" :disabled="isUploading">
                  <span v-if="isUploading">
                    <i class="fas fa-spinner fa-spin me-2"></i>Subiendo...
                  </span>
                  <span v-else>
                    <i class="fas fa-cloud-upload-alt me-2"></i>Subir Video 
                  </span>
                </button>
                <div v-else class="text-center py-4">
                  <p class="text-muted">No hay video disponible para esta lección.</p>
                </div>
              </div>
            </template>


            <!-- CASE 2: Video exists → Show "Eliminar Video" -->
            <button v-else @click="removeVideo" class="btn btn-danger w-100">
              <i class="fas fa-trash-alt me-2"></i>Eliminar Video
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.video-modal {
  width: 80vw;
  max-width: 800px;
}

.video-container {
  position: relative;
  padding-bottom: 56.25%;
  /* 16:9 aspect ratio */
  margin: 1rem 0;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.video-container video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.video-controls {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
}

.file-upload-container {
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
}

.test-video-container {
  max-width: 800px;
  margin: 0 auto;
}
</style>